<template>
  <div class="bg-white rounded-lg mt-3 p-4">
    <div class="flex gap-4 mb-2">
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon v-bind="attrs" v-on="on">mdi-help-circle</v-icon>
        </template>
        <span>
            Some alerts like vitals may be cleared immediately. Others like No Recent Readings (NRR) may only update every morning.
          </span>
      </v-tooltip>
    </div>
    <div class="flex gap-4 mb-2">
      <!-- Alert Type Filter -->
      <v-select
          v-model="selectedAlertType"
          :items="alertTypeOptions"
          label="Filter by Alert Type"
          style="max-width: 300px;"
      ></v-select>

      <!-- Severity Filter — only shown when Vitals is selected -->
      <v-select
          v-if="selectedAlertType === 'vitals'"
          v-model="selectedSeverity"
          :items="['All', 'Alert', 'Warning']"
          label="Severity"
          style="max-width: 220px;"
      ></v-select>
    </div>

    <v-data-table :headers="dtHeaders" :items="filteredAlerts" sort-by="created_at" sort-desc class="v-data-table">
      <template v-slot:[`item.alert_severity`]="{ item }">
        <v-chip v-if="item.alert_severity" :color="item.alert_severity === 'alert' ? 'red' : 'yellow'" small class="capitalize ml-1">{{ item.alert_severity }}</v-chip>
      </template>
      <template v-slot:[`item.alert_type_label`]="{ item }">
        <v-tooltip bottom :disabled="!item.description">
          <template v-slot:activator="{ on, attrs }">
            <v-chip v-bind="attrs" v-on="on" :color="item.type_color" small :class="alertChipTextClass(item.type_color)">{{ item.alert_type_label }}</v-chip>
          </template>
          <span>{{ item.description }}</span>
        </v-tooltip>
      </template>
      <template v-slot:[`item.created_at`]="{ item }">{{ item.created_at | datetime }}</template>
      <template v-slot:[`item.actions`]="{ item }">
        <template v-if="item.is_vital">
          <button type="button" @click.stop="openResolveVitalAlertModal(item.raw_alert)" class="blue_button josefin_font ml-3 p-1" :disabled="loading">Resolve</button>
          <button v-if="item.alert_severity === 'warning'" type="button" @click.stop="autoResolveVitalAlert(item.raw_alert)" class="blue_button josefin_font ml-3 p-1 m-1" :disabled="loading">Auto Resolve</button>
        </template>
      </template>
    </v-data-table>

    <el-dialog :close-on-click-modal="false" :title="'Resolve Vital Alert'" :visible.sync="resolveVitalAlertModalVisible" width="50%" @close="closeResolveVitalAlertModal()">
      <div class="dialog__body" ref="modalResolveVitalAlert">
        <RpmDashboardErrors v-bind:errors="resolveVitalAlertErrors" v-bind:title="'Resolve Vital Alert Errors'"></RpmDashboardErrors>

        <div class="mb-4">
          <label class="block text-gray-700 text-lg font-bold mb-1">Patient</label>
          <p>{{ patient.first_name }} {{ patient.last_name }} </p>
        </div>

        <div class="mb-4">
          <label class="block text-gray-700 text-lg font-bold mb-1">Alert</label>
          <p><strong>Health Condition:</strong> {{ resolveVitalAlert.care_plan.health_condition }}</p>
          <p><strong>Notes:</strong> {{ resolveVitalAlert.care_plan.notes }}</p>
          <div v-if="resolveVitalAlert.vital_reading.attribute !== 'irregular_heartbeat'">
            <p><strong><span v-if="resolveVitalAlert.care_plan.alerts.find((carePlanAlert) => carePlanAlert.type === resolveVitalAlert.vital_reading.attribute).type === 'weight'">Max Lost</span><span v-else>Min:</span></strong> {{ resolveVitalAlert.care_plan.alerts.find((carePlanAlert) => carePlanAlert.type === resolveVitalAlert.vital_reading.attribute).min }} </p>
            <p><strong><span v-if="resolveVitalAlert.care_plan.alerts.find((carePlanAlert) => carePlanAlert.type === resolveVitalAlert.vital_reading.attribute).type === 'weight'">Max Gained</span><span v-else>Max:</span></strong> {{ resolveVitalAlert.care_plan.alerts.find((carePlanAlert) => carePlanAlert.type === resolveVitalAlert.vital_reading.attribute).max }} </p>
          </div>
          <p class="capitalize">{{ resolveVitalAlert.vital_reading.attribute }}: {{ resolveVitalAlert.vital_reading.value }}</p>
        </div>

        <div class="mb-4">
          <label class="block text-gray-700 text-sm font-bold mb-2" for="resolving_action"> Resolving Action </label>
          <textarea class="h-40 shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" id="resolving_action" type="text" v-model="resolveVitalAlertAction"/>
        </div>
        <div class="mb-4">
          <label class="block text-gray-700 text-sm font-bold mb-2" for="taskDuration">Duration in minutes <span class="required">*</span></label>
          <input v-model="taskDuration" type="number" interval="1" min="0" id="taskDuration" required class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"/>
        </div>
        <span slot="footer" class="josefin dialog-footer">
          <el-button @click="saveResolveVitalAlert" type="primary" :disabled="loading">Resolve Alert</el-button>
        </span>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {mapState} from "vuex";
import RpmDashboardErrors from "@/components/RpmDashboardErrors.vue";
import Vue from "vue";
import Template from "@/mixins/Template";
import AlertTypesMixin from "@/mixins/AlertTypes";

export default {
  mixins: [AlertTypesMixin],
  components: {Template, RpmDashboardErrors},
  props: {
    patient: Object,
  },
  data() {
    return {
      page: 0,
      pageCount: 0,
      loading: false,
      selectedAlertType: 'all',
      selectedSeverity: "All",
      resolveVitalAlertErrors: [],
      resolveVitalAlertModalVisible: false,
      resolveVitalAlertAction: "",
      taskDuration: 0,
      resolveVitalAlert: {
        care_plan: {
          notes: "",
          alerts: [{ type: "", min: "", max: "" }],
        },
        vital_reading: { attribute: "", value: "" },
      },
    };
  },
  computed: {
    ...mapState(["patients"]),

    dtHeaders() {
      return [
        {text: 'Alert Type', value: 'alert_type_label'},
        {text: 'Severity', value: 'alert_severity'},
        {text: 'Health Condition', value: 'health_condition'},
        {text: 'Notes', value: 'notes'},
        {text: 'Min', value: 'min', width: 80},
        {text: 'Max', value: 'max', width: 80},
        {text: 'Value', value: 'value', width: 80},
        {text: 'Date', value: 'created_at', width: 180},
        {text: 'Actions', value: 'actions', sortable: false},
      ];
    },

    vitalRows() {
      const vitals = (this.patient.vital_alerts_unresolved || []).concat(this.patient.vital_warnings_unresolved || []);
      return vitals.map((alert) => {
        const category = alert.severity === 'warning' ? 'vital_warning' : 'vital';
        return {
          is_vital: true,
          alert_category: category,
          alert_type_label: this.alertLabel(category),
          type_color: this.alertColor(category),
          description: this.alertDescription(category),
          alert_severity: alert.severity,
          health_condition: alert.care_plan ? alert.care_plan.health_condition : '',
          notes: alert.care_plan ? alert.care_plan.notes : '',
          min: this.getAlertMin(alert),
          max: this.getAlertMax(alert),
          value: alert.vital_reading ? alert.vital_reading.value : '',
          created_at: alert.created_at,
          raw_alert: alert,
        };
      });
    },

    nonVitalRows() {
      const cacheAlerts = (this.patient.cache && this.patient.cache.alerts) ? this.patient.cache.alerts : {};
      const rows = [];
      for (const [type, date] of Object.entries(cacheAlerts)) {
        if (type === 'vital' || type === 'vital_warning') continue;
        rows.push({
          is_vital: false,
          alert_category: type,
          alert_type_label: this.alertLabel(type),
          type_color: this.alertColor(type),
          description: this.alertDescription(type),
          alert_severity: null,
          health_condition: '',
          notes: '',
          min: '',
          max: '',
          value: '',
          created_at: date,
          raw_alert: null,
        });
      }
      return rows;
    },

    allAlerts() {
      return this.vitalRows.concat(this.nonVitalRows);
    },

    alertTypeOptions() {
      const options = [{ value: 'all', text: 'All' }];
      if (this.vitalRows.length) {
        options.push({ value: 'vitals', text: 'Vitals' });
      }
      const nonVitalTypes = [...new Set(this.nonVitalRows.map(a => a.alert_category))];
      nonVitalTypes.forEach(t => {
        options.push({ value: t, text: this.alertLabel(t) });
      });
      return options;
    },

    filteredAlerts() {
      if (!this.selectedAlertType || this.selectedAlertType === 'all') return this.allAlerts;

      if (this.selectedAlertType === 'vitals') {
        let rows = this.vitalRows;
        if (this.selectedSeverity === 'Alert') {
          rows = rows.filter(a => a.alert_severity === 'alert');
        } else if (this.selectedSeverity === 'Warning') {
          rows = rows.filter(a => a.alert_severity === 'warning');
        }
        return rows;
      }

      return this.nonVitalRows.filter(a => a.alert_category === this.selectedAlertType);
    },
  },
  methods: {
    prepareVitalAlertForResolve(alert) {
      this.resolveVitalAlert = alert;
      let action = "Health Condition: " + this.resolveVitalAlert.care_plan.health_condition + "\n";
      action += "Care Plan: " + this.resolveVitalAlert.care_plan.notes + "\n";
      if (this.resolveVitalAlert.vital_reading.attribute !== 'irregular_heartbeat') {
        action += "Min: " + this.getAlertMin(this.resolveVitalAlert) + "\n";
        action += "Max: " + this.getAlertMax(this.resolveVitalAlert) + "\n";
      }
      action += this.resolveVitalAlert.vital_reading.attribute.charAt(0).toUpperCase() + this.resolveVitalAlert.vital_reading.attribute.slice(1) + ": " + this.resolveVitalAlert.vital_reading.value + "\n";
      action += "Resolving Action:\n";
      this.resolveVitalAlertAction = action;
    },
    autoResolveVitalAlert(alert) {
      this.prepareVitalAlertForResolve(alert);
      this.resolveVitalAlertAction += "Reviewed Patients Matrix";
      this.taskDuration = 1;
      this.saveResolveVitalAlert();
    },
    openResolveVitalAlertModal(alert) {
      this.prepareVitalAlertForResolve(alert);
      this.resolveVitalAlertModalVisible = true;
    },
    closeResolveVitalAlertModal() {
      this.resolveVitalAlertModalVisible = false;
    },
    checkVitalsAlertsForm() {
      const required = ["Duration"];
      this.resolveVitalAlertErrors = this.checkForm(required, { Duration: this.taskDuration });
      return !this.resolveVitalAlertErrors.length;
    },
    checkForm(required, formValuesObj) {
      return required.filter((key) => !formValuesObj[key]).map((key) => key.replace("_", " ") + " is required.");
    },
    async saveResolveVitalAlert() {
      if (this.loading) return;
      if (!this.checkVitalsAlertsForm()) {
        this.$nextTick(() => this.$refs.modalResolveVitalAlert.scrollIntoView());
        return;
      }
      this.loading = true;
      Vue.$http.post(`/api/rpm_ccm_dashboard/resolve_vital_alert`,
          {id: this.resolveVitalAlert.id, resolving_action: this.resolveVitalAlertAction, duration: this.taskDuration},
          {params: {token: await this.$auth.getTokenSilently()}}
      ).then(() => {
        this.$emit('get-patient-details');
        this.closeResolveVitalAlertModal();
      }).catch((error) => this.handleEhrApiErrors(error, this.resolveVitalAlertErrors, this.$refs.modalResolveVitalAlert))
          .finally(() => this.loading = false);
    },
    handleEhrApiErrors(errorResponse, vueErrors, ref) {
      let response = errorResponse.response.data;
      if (response.errors) {
        for (const key in response.errors) {
          if (response.errors.hasOwnProperty(key)) {
            for (const fieldKey in response.errors[key]) {
              if (response.errors[key].hasOwnProperty(fieldKey)) {
                vueErrors.push(key + ": " + response.errors[key][fieldKey]);
              }
            }
          }
        }
        this.$nextTick(() => ref.scrollIntoView());
      }
    },
    getAlertMin(alert) {
      if (!alert.care_plan || !alert.vital_reading || !alert.care_plan.alerts) return undefined;
      const carePlanAlert = alert.care_plan.alerts.find(ca => ca.type === alert.vital_reading.attribute);
      return carePlanAlert ? carePlanAlert.min : undefined;
    },
    getAlertMax(alert) {
      if (!alert.care_plan || !alert.vital_reading || !alert.care_plan.alerts) return undefined;
      const carePlanAlert = alert.care_plan.alerts.find(ca => ca.type === alert.vital_reading.attribute);
      return carePlanAlert ? carePlanAlert.max : undefined;
    },
  },
  filters: {
    duration(value) {
      if (!value) return "";
      value = Number(value);
      const h = Math.floor(value / 60);
      const m = value % 60;
      return (h ? `${h}hr ` : "") + `${m}min`;
    },
  },
};
</script>

